import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { User } from 'pdauth';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';

import { environment } from '../../../environment';
import { AoiService } from '../../../services/aoi.service';
import { GisToolsService } from '../../../services/gis-tools.service';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../../services/interfaces/IUserSettingsService';
import { BaseComponent } from '../../base.component';
import { UserSettingsComponent } from '../../userSettings/user-settings.component';

/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-region',
    templateUrl: './region.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './region.component.scss']
})
export class RegionComponent extends BaseComponent implements AfterViewInit {
    private readonly SUPPORTED_DEFAULT_REGIONS = ['county', 'district'];

    @Input()
    debounceTime: number = 200;

    @Input()
    maxSuggestionsNumber: number = 10;

    selectedCounty: any;
    selectedSchoolCounty: any;
    filteredMunicipalties: any[];
    filteredschoolDistricts: any[];

    @Input()
    districts: any[];
    @Input()
    counties: any[];
    @Input()
    municipalities: any[];
    @Input()
    schoolDistricts: any[];
    @Input()
    planningPartners: any[];
    @Input()
    houseDistricts: any[];
    @Input()
    senateDistricts: any[];
    @Input()
    congressionalDistricts: any[];
    @Input()
    tsmoRegions: any[];
    @Input()
    zipCodes: any[];

    @Input()
    countySortField: string;

    @Output() readonly loaded: EventEmitter<any[]> = new EventEmitter();
    @Input()
    public get AoiService() {
        return this.aoiService;
    }

    showTSMO: boolean = false;
    @Input() showZipCodes: boolean = false;
    selectedZip: string = '';

    get canSaveDefaultSelection(): boolean {
        return this.SUPPORTED_DEFAULT_REGIONS.includes(this.activePicker.value);
    }

    private _selection: any;
    set selection(value: any) {
        this._selection = value;
        if (this.searchText.value.length > 0) {
            this.populateSearchResults(this.searchText.value);
        }
    }

    get selection() {
        return this._selection;
    }

    loading: boolean = false;

    @Input()
    selections: any[] = [];

    @Input()
    hideRegionSearch: boolean = false;

    @Input()
    showPenndotLabel: boolean = false;

    activePicker: FormControl = new FormControl('county');
    searchText: FormControl = new FormControl('');
    setDefaults: FormControl = new FormControl(false);

    searchResults: any[] = [];
    omitted: number;
    isActive: boolean;
    currentUser: User;

    hasDefaultSelection$ = new BehaviorSubject<boolean>(false);
    isFetchingUserSettings = false;
    userSettings;
    userSettingsComponent: UserSettingsComponent;

    constructor(
        _vcr: ViewContainerRef,
        private readonly gisToolsService: GisToolsService,
        private readonly aoiService: AoiService,
        private readonly cdr: ChangeDetectorRef,
        @Inject('authenticationService') private readonly authService: IAuthenticationService,
        @Inject('LoggingService') private readonly loggingService: ILoggingService,
        @Inject('UserSettingsService') private readonly userSettingsService: IUserSettingsService
        ) {
        super(_vcr, true);
    }

    ngAfterViewInit(): void {
        this.activePicker.valueChanges.subscribe(_ => {
            this.selections = [];
            this.setDefaults.setValue(false);
            this.cdr.detectChanges();
        });

        this.authService.permissions.subscribe(permissions => {
            this.showTSMO = permissions
                && (permissions.includes('TSMO Layers')
                    || permissions.includes('TSMO Non-Restricted Data')
                    || permissions.includes('TSMO Restricted Data')
                    || permissions.includes('TSMO Data'));
        });

        this.searchText.valueChanges.pipe(
            debounceTime(this.debounceTime),
            distinctUntilChanged()
        ).subscribe(text => {
            this.populateSearchResults(text);
        });

        this.authService.GetCurrentUser().subscribe(user => {
            if (user) {
                this.currentUser = user;

                if (this.isActive) {
                    this.loadUserSettings();
                }
            } else {
                this.currentUser = null;
                this.userSettings = null;
                this.hasDefaultSelection$.next(false);
            }
        });

        this.isReady();
    }

    private async loadUserSettings() {
        if (!this.currentUser) {
            return;
        }

        this.isFetchingUserSettings = true;

        if (!this.userSettingsComponent) {
            const parentScope = await this.getClosestAncestor('mc-scope');

            try {
                this.userSettingsComponent = await parentScope.getComponent('mc-user-settings');
            } catch (error) {
                return;
            }
        }

        this.userSettingsService.GetSettingValue('userSettings', environment.pdauthAppName, this.currentUser.emailAddress).pipe(take(1)).subscribe(settings => {
            this.userSettings = settings;
            this.isFetchingUserSettings = false;
            this.hasDefaultSelection$.next(
                settings.counties && settings.counties.length > 0
                || settings.districts && settings.districts.length > 0);

            this.cdr.detectChanges();
        });
    }

    @Input()
    public activate = async () => {
        if (!this.isFetchingUserSettings) {
            await this.loadUserSettings();
        }
        this.setDefaults.setValue(false);
        if (this.isActive) { return; }
        await this.loadData(this.activePicker.value);
        this.isActive = true;
    }

    @Input()
    public reset = () => {
        this.selection = null;
        this.selections = [];
        this.selectedCounty = null;

    }

    private async loadData(region: string) {
        try {
            if (!region) {
                region = this.activePicker.value;
            }

            let args = [
                { regionName: 'district', val: 0 },
                { regionName: 'county', val: 1 },
                { regionName: 'municipality', val: 2 },
                { regionName: 'planningPartner', val: 3 },
                { regionName: 'houseDistrict', val: 4 },
                { regionName: 'senateDistrict', val: 5 },
                { regionName: 'congressionalDistrict', val: 6 },
                { regionName: 'zipCode', val: 7 },
                { regionName: 'schoolDistrict', val: 8}
            ];
            args = args.filter(reg => reg.regionName === region);
            if (this.checkIfLoaded(args[0].regionName)) {
                return; //data is already loaded
            }
            const regionIndex = args[0].val;
            const returnMapping = [
                (d => ({ type: 'district', title: `District ${d.districtNumber}`, ...d })),
                (c => ({ type: 'county', title: `${c.name} COUNTY (${c.countyCode})`, ...c })),
                (m => ({ type: 'municipality', title: `${m.name} ${m.municipalityClass ? m.municipalityClass.includes('TWP') ? 'TOWNSHIP' : m.municipalityClass.toUpperCase() : ''} (${m.municipalityCode})`, ...m })),
                (p => ({ type: 'planningPartner', title: `${p.agencyType ? p.agencyType.toUpperCase() : ''}: ${p.planningPartnerShortName}`, ...p })),
                (h => ({ type: 'houseDistrict', title: `${h.lastName} ${h.firstName} (District ${h.houseDistrictNumber})`, ...h })),
                (s => ({ type: 'senateDistrict', title: `${s.lastName} ${s.firstName} (District ${s.senateDistrictNumber})`, ...s })),
                (c => ({ type: 'congressionalDistrict', title: `${c.lastName} ${c.firstName} (District ${c.congressionalDistrictNumber})`, ...c })),
                (z => ({ type: 'zipCode', title: `${z.zipCode}`, ...z })),
                (sd => ({ type: 'schoolDistrict', title: `${sd.schoolDistrictName}` , ...sd }))
            ];

            const regionRequests = [];
            switch (region) {
                case 'district':
                    regionRequests.push(this.gisToolsService.getAllDistricts('districtNumber'));
                    break;
                case 'county':
                    regionRequests.push(this.gisToolsService.getAllCounties('countyCode, paCtyCode, name', this.countySortField));
                    break;
                case 'municipality':
                    regionRequests.push(this.gisToolsService.getAllMunicipalities('countyCode, municipalityCode, municipalityClass, name'));
                    break;
                case 'planningPartner':
                    regionRequests.push(this.gisToolsService.getAllPlanningPartners('planningPartnerShortName, agencyType'));
                    break;
                case 'houseDistrict':
                    regionRequests.push(this.gisToolsService.getAllHouseDistricts('firstName, lastName, legislativeDistrictNumber'));
                    break;
                case 'senateDistrict':
                    regionRequests.push(this.gisToolsService.getAllSenateDistricts('firstName, lastName, legislativeDistrictNumber'));
                    break;
                case 'congressionalDistrict':
                    regionRequests.push(this.gisToolsService.getAllCongressionalDistricts('firstName, lastName, legislativeDistrictNumber'));
                    break;
                case 'zipCode':
                    regionRequests.push(this.gisToolsService.getAllZipCodes('zip'));
                    break;
                case 'schoolDistrict':
                    regionRequests.push(this.gisToolsService.getAllSchoolDistricts('countyCode,schoolDistrictName'));
                    break;
                default:
                    break;
            }

            [
                this.districts,
                this.counties,
                this.municipalities,
                this.planningPartners,
                this.houseDistricts,
                this.senateDistricts,
                this.congressionalDistricts,
                this.zipCodes,
                this.schoolDistricts
            ] = await Promise.all(regionRequests).then(results => {
                for (let i = 0; i < returnMapping.length; i++) {
                    if (i !== regionIndex) {
                        switch (i) {
                            case 0:
                                results.splice(i, 0, this.districts);
                                break;
                            case 1:
                                results.splice(i, 0, this.counties);
                                break;
                            case 2:
                                results.splice(i, 0, this.municipalities);
                                break;
                            case 3:
                                results.splice(i, 0, this.planningPartners);
                                break;
                            case 4:
                                results.splice(i, 0, this.houseDistricts);
                                break;
                            case 5:
                                results.splice(i, 0, this.senateDistricts);
                                break;
                            case 6:
                                results.splice(i, 0, this.congressionalDistricts);
                                break;
                            case 7:
                                results.splice(i, 0, this.zipCodes);
                                break;
                            case 8:
                                results.splice(i, 0, this.schoolDistricts);
                                break;
                            default:
                                break;
                        }
                    }
                }
                return results.map(result => {
                    return result ? result.map(returnMapping[regionIndex]) : undefined;
                });
            });

            this.tsmoRegions = environment.tsmoRegions;
            this.loaded.emit([this.districts, this.counties, this.municipalities, this.planningPartners, this.houseDistricts, this.senateDistricts, this.congressionalDistricts]);
        } catch (error) {
            this.loggingService.Log('Error retrieving AOI filter data', error);
        }
        this.cdr.detectChanges();
        if (region === 'municipality' && this.counties && this.selectedCounty === undefined) {
            this.selectedCounty = this.counties[0];
            this.filterMunicipalities(this.selectedCounty);
        }
        if (region === 'schoolDistrict' && this.counties && this.selectedSchoolCounty === undefined) {
            this.selectedSchoolCounty = this.counties[0];
            this.filterSchoolDistrict(this.selectedSchoolCounty);
        }
    }

    addSelectionFromSearch(selection) {
        this.addSelection(selection);
        this.searchText.setValue('');
    }

    addSelection(selection) {
        this.selection = selection;
        this.selections.push(selection);

        const flags = {};
        this.selections = this.selections.filter(s => {
            if (flags[s.title]) {
                return false;
            }
            flags[s.title] = true;
            return true;
        });
    }

    /**
     * Selects a region tab and sets its default selections.
     * @param region The region tab to make selections in.
     * @returns True if the region tab is supported and has default selections. Otherwise, returns false.
     */
    private async useDefaultSelectionForRegion(region: string) {
        let defaultSelections: string[];
        let items: any[];
        let getItemCode: (x: any) => string;

        await this.loadData(region);

        switch (region) {
            case 'district':
                defaultSelections = this.userSettings.districts;
                items = this.districts;
                getItemCode = d => d.districtNumber;
                break;

            case 'county':
                defaultSelections = this.userSettings.counties;
                items = this.counties;
                getItemCode = c => c.countyCode;
                break;

            default:
                return false;
        }

        if (!defaultSelections || defaultSelections.length === 0) {
            return false;
        }

        this.activePicker.setValue(region);
        defaultSelections = defaultSelections.map(x => x.split('-')[1]);
        this.selections = items.filter(x => defaultSelections.includes(getItemCode(x)));

        return true;
    }

    async useDefaultSelection() {
        const regions = this.SUPPORTED_DEFAULT_REGIONS.slice();

        // Try to load the default selections for the currently selected tab first.
        // The first region with default selections is the one we go with.
        regions.unshift(this.activePicker.value);

        for (const region of regions) {
            if (await this.useDefaultSelectionForRegion(region)) {
                break;
            }
        }
    }

    async setDefaultSelection() {
        if (!this.userSettings) {
            this.userSettings = {};
        }

        switch (this.activePicker.value) {
            case 'district':
                this.userSettings.districts = this.selections.map(x => `district-${x.districtNumber}`) as any;
                break;

            case 'county':
                this.userSettings.counties = this.selections.map(x => `county-${x.countyCode}`) as any;
                break;

            default:
                return;
        }

        await this.userSettingsService.SetSettingValue(
            'userSettings',
            this.userSettings,
            this.currentUser.emailAddress,
            environment.pdauthAppName);
    }

    removeSelection(selection) {
        this.selections = this.selections.filter(s => s !== selection);
        if (this.selections.length === 0) {
            this.selection = null;
        }
    }

    filterMunicipalities(county) {
        this.selection = null;
        this.selections = [];
        if (!this.checkIfLoaded('municipality')) {
            this.loadData('municipality');
        }
        this.filteredMunicipalties = this.municipalities.filter(m => m.countyCode === county.countyCode);
        this.selectedCounty = county;
    }

    filterSchoolDistrict(county) {
        this.selection = null;
        this.selections = [];
        if (!this.checkIfLoaded('schoolDistrict')) {
            this.loadData('schoolDistrict');
        }
        this.filteredschoolDistricts = this.schoolDistricts.filter(sd => sd.countyCode === county.paCtyCode);
        this.selectedSchoolCounty = county;
    }

    private populateSearchResults(text) {
        if (!text) {
            this.searchResults = [];
            this.omitted = 0;
            return;
        }

        this.searchResults = [
            this.districts,
            this.counties,
            this.municipalities,
            this.planningPartners,
            this.senateDistricts,
            this.houseDistricts,
            this.congressionalDistricts
        ].flat()
            .filter(r => r.title.toLowerCase().includes(text.toLowerCase()));

        if (this.selections.length > 0) {
            this.searchResults = this.searchResults.filter(r => r.type === this.selections[0].type);
        }

        if (this.searchResults.length > this.maxSuggestionsNumber + 1) {
            this.omitted = this.searchResults.length - this.maxSuggestionsNumber;
            this.searchResults = this.searchResults.slice(0, this.maxSuggestionsNumber);
        }
    }

    @Input()
    public addResultToMap = () => {
        if (this.activePicker.value === 'zipCodes') {
            this.selections = [{ type: 'zipCode', zip: this.selectedZip, title: this.selectedZip }];
        }

        if (!this.selections || this.selections.length === 0) {
            return;
        }

        this.aoiService.setSearchCriterias(this.selections);

        if (this.setDefaults.value) {
            this.setDefaultSelection();
        }
    }

    private checkIfLoaded(region: string) {
        switch (region) {

            case 'district':
                return this.districts && this.districts.length > 0;
            case 'county':
                return this.counties && this.counties.length > 0;
            case 'municipality':
                return this.municipalities && this.municipalities.length > 0;
            case 'planningPartner':
                return this.planningPartners && this.planningPartners.length > 0;
            case 'houseDistrict':
                return this.houseDistricts && this.houseDistricts.length > 0;
            case 'senateDistrict':
                return this.senateDistricts && this.senateDistricts.length > 0;
            case 'congressionalDistrict':
                return this.congressionalDistricts && this.congressionalDistricts.length > 0;
            case 'zipCode':
                return this.zipCodes && this.zipCodes.length > 0;
            case 'schoolDistrict':
                return this.schoolDistricts && this.schoolDistricts.length > 0;
            default:
                return false;
        }
    }
}
